import InputValidator from "./InputValidator";

/**
 * Adapter Pattern: Wraps existing InputValidator into new validation system
 * This allows us to use legacy validators while maintaining new architecture
 */
class InputValidatorAdapter {
    static email(value) {
        if (!value) return null;
        const error = InputValidator.isValidEmail(value);
        return error || null;
    }

    static loginEmail(value) {
        if (!value) return null;
        const error = InputValidator.isValidLoginEmail(value);
        return error || null;
    }

    static password(value) {
        if (!value) return null;
        const error = InputValidator.isValidPassword(value);
        return error || null;
    }

    static panNumber(value) {
        if (!value) return null;
        const error = InputValidator.isPanNumber(value);
        return error || null;
    }

    static aadhaar(value) {
        if (!value) return null;
        const error = InputValidator.isValidAadhaar(value);
        return error || null;
    }

    static passport(value) {
        if (!value) return null;
        const error = InputValidator.isValidPassport(value);
        return error || null;
    }

    static workAuthNumber(value) {
        if (!value) return null;
        const error = InputValidator.isValidWorkAuthNumber(value);
        return error || null;
    }
}

const isExistingDocumentURL = (value) => {
    return typeof value === 'string' && (
        value.startsWith('data:') ||
        value.startsWith('http://') ||
        value.startsWith('https://') ||
        value.startsWith('/')
    );
};

export const hasValidFile = (fileList) => {
 
    if (isExistingDocumentURL(fileList)) {
        return true;
    }
    
    if (!fileList) return false;
    if (!Array.isArray(fileList)) return false;
    if (fileList.length === 0) return false;
    
    return fileList.some(file => 
        !file.status || 
        file.status === 'done' || 
        file.originFileObj
    );
};


export const VALIDATION_RULES = {
    // Basic validations
    REQUIRED: (value) => {
        const error = InputValidator.isEmpty(value);
        return error || null;
    },
   
    MIN_LENGTH: (min) => (value) => {
        if (!value) return null;
        return value.length >= min ? null : `Minimum ${min} characters required`;
    },

    MAX_LENGTH: (max) => (value) => {
        if (!value) return null;
        return value.length <= max ? null : `Maximum ${max} characters allowed`;
    },

    EXACT_LENGTH: (length) => (value) => {
        if (!value) return null;
        return value.length === length ? null : `Must be exactly ${length} characters`;
    },

    // Email validations
    EMAIL: (value) => InputValidatorAdapter.email(value),

    LOGIN_EMAIL: (value) => InputValidatorAdapter.loginEmail(value),

    // Password validation
    PASSWORD: (value) => InputValidatorAdapter.password(value),

    STRONG_PASSWORD: (value) => {
        if (!value) return null;
        const baseError = InputValidator.isValidPassword(value);
        if (baseError) return baseError;

        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumber = /[0-9]/.test(value);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value);

        if (!hasUpperCase) return 'Password must contain at least one uppercase letter';
        if (!hasLowerCase) return 'Password must contain at least one lowercase letter';
        if (!hasNumber) return 'Password must contain at least one number';
        if (!hasSpecial) return 'Password must contain at least one special character';

        return null;
    },

    // Phone validation (enhanced)
    PHONE: (value) => {
        if (!value) return null;
        const cleaned = value.replace(/[\s-()]/g, '');
        const phoneRegex = /^\+?1?\d{10,14}$/;
        return phoneRegex.test(cleaned) ? null : 'Invalid phone number format';
    },

    INDIAN_PHONE: (value) => {
        if (!value) return null;
        const cleaned = value.replace(/[\s-()]/g, '');
        const indianPhoneRegex = /^[6-9]\d{9}$/;
        return indianPhoneRegex.test(cleaned) ? null : 'Invalid Indian phone number (10 digits starting with 6-9)';
    },

    // Government ID validations
    PAN: (value) => InputValidatorAdapter.panNumber(value),

    AADHAAR: (value) => InputValidatorAdapter.aadhaar(value),

    PASSPORT: (value) => InputValidatorAdapter.passport(value),

    WORK_AUTH_NUMBER: (value) => InputValidatorAdapter.workAuthNumber(value),

    // Tax and business IDs
    EIN: (value) => {
        if (!value) return null;
        const einRegex = /^\d{2}-?\d{7}$/;
        return einRegex.test(value) ? null : 'valid EIN format (XX-XXXXXXX)';
    },


    TAX_ID: (value) => {
        if (!value) return null;
        const taxIdRegex = /^\d{9}$/;
        return taxIdRegex.test(value) ? null : 'Valid Format (Ex : 123456789)';
    },

    GST: (value) => {
        if (!value) return null;
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        return gstRegex.test(value) ? null : 'Invalid GST format';
    },


    ZIP_CODE: (value) => {
        if (!value) return null;
        const zipRegex = /^\d{5,6}$/;
        return zipRegex.test(value) ? null : 'Invalid ZIP code format';
    },

    INDIAN_PINCODE: (value) => {
        if (!value) return null;
        const pincodeRegex = /^[1-9][0-9]{5}$/;
        return pincodeRegex.test(value) ? null : 'Invalid pincode (6 digits, cannot start with 0)';
    },


    ALPHA_ONLY: (value) => {
        if (!value) return null;
        const alphaRegex = /^[a-zA-Z\s]+$/;
        return alphaRegex.test(value) ? null : 'Only letters allowed';
    },

    ALPHANUMERIC: (value) => {
        if (!value) return null;
        const alphanumericRegex = /^[a-zA-Z0-9\s]+$/;
        return alphanumericRegex.test(value) ? null : 'Only letters and numbers allowed';
    },

    NO_SPECIAL_CHARS: (value) => {
        if (!value) return null;
        const noSpecialRegex = /^[a-zA-Z0-9\s]+$/;
        return noSpecialRegex.test(value) ? null : 'Special characters not allowed';
    },


    NUMERIC: (value) => {
        if (!value) return null;
        return /^\d+$/.test(value) ? null : 'Only numbers allowed';
    },

    MIN_VALUE: (min) => (value) => {
        if (!value) return null;
        const numValue = parseFloat(value);
        return numValue >= min ? null : `Value must be at least ${min}`;
    },

    MAX_VALUE: (max) => (value) => {
        if (!value) return null;
        const numValue = parseFloat(value);
        return numValue <= max ? null : `Value must be at most ${max}`;
    },

    RANGE: (min, max) => (value) => {
        if (!value) return null;
        const numValue = parseFloat(value);
        if (numValue < min || numValue > max) {
            return `Value must be between ${min} and ${max}`;
        }
        return null;
    },

    // Date validations
    MIN_DATE: (minDate) => (value) => {
        if (!value) return null;
        const date = new Date(value);
        const min = new Date(minDate);
        return date >= min ? null : `Date must be on or after ${min.toLocaleDateString()}`;
    },

    MAX_DATE: (maxDate) => (value) => {
        if (!value) return null;
        const date = new Date(value);
        const max = new Date(maxDate);
        return date <= max ? null : `Date must be on or before ${max.toLocaleDateString()}`;
    },

    MIN_AGE: (minAge) => (value) => {
        if (!value) return null;
        const birthDate = new Date(value);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            return age - 1 >= minAge ? null : `Must be at least ${minAge} years old`;
        }

        return age >= minAge ? null : `Must be at least ${minAge} years old`;
    },

    // File validations
    FILE_REQUIRED: (fileList) => {
         if (isExistingDocumentURL(fileList)) {
            return null;
        }
        if (!hasValidFile(fileList)) {
            return 'File is required';
        }
        // const isEmpty = InputValidator.isArrayEmpty(fileList);
        // return isEmpty ? 'File is required' : null;
    },

    FILE_TYPE: (allowedTypes) => (fileList) => {
        if (isExistingDocumentURL(fileList)) {
            return null;
        }
        
        if (!fileList || fileList.length === 0) return null;
        const file = fileList[0]?.originFileObj || fileList[0];
        if (!file) return null;

        const fileType = file.type || '';
        const fileName = file.name || '';
        const fileExtension = fileName.split('.').pop()?.toLowerCase();

        const isValid = allowedTypes.some(type =>
            fileType.includes(type) || fileExtension === type.replace('.', '')
        );

        return isValid ? null : `File must be one of: ${allowedTypes.join(', ')}`;
    },

    FILE_SIZE: (maxSizeMB) => (fileList) => {
        
         if (isExistingDocumentURL(fileList)) {
            return null;
        }
        if (!fileList || fileList.length === 0) return null;
        const file = fileList[0]?.originFileObj || fileList[0];
        if (!file) return null;

        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        return file.size <= maxSizeBytes
            ? null
            : `File size must be less than ${maxSizeMB}MB`;
    },

    // Cross-field validations
    MATCH_FIELD: (fieldName, label) => (value, formData) => {
        if (!value) return null;
        return value === formData[fieldName]
            ? null
            : `Must match ${label}`;
    },

    DEPENDS_ON: (fieldName, label) => (value, formData) => {
        if (!formData[fieldName]) {
            return `Please select ${label} first`;
        }
        return null;
    },

    // URL validation
    URL: (value) => {
        if (!value) return null;
        try {
            new URL(value);
            return null;
        } catch {
            return 'Invalid URL format';
        }
    },

    // Custom validator
    CUSTOM: (validatorFn) => validatorFn,
};

// Validator composer - follows Open/Closed Principle
export class FieldValidator {
    constructor(fieldName, label) {
        this.fieldName = fieldName;
        this.label = label;
        this.rules = [];
    }

    required() {
        this.rules.push(VALIDATION_RULES.REQUIRED);
        return this;
    }

    minLength(min) {
        this.rules.push(VALIDATION_RULES.MIN_LENGTH(min));
        return this;
    }

    maxLength(max) {
        this.rules.push(VALIDATION_RULES.MAX_LENGTH(max));
        return this;
    }

    exactLength(length) {
        this.rules.push(VALIDATION_RULES.EXACT_LENGTH(length));
        return this;
    }

    email() {
        this.rules.push(VALIDATION_RULES.EMAIL);
        return this;
    }

    loginEmail() {
        this.rules.push(VALIDATION_RULES.LOGIN_EMAIL);
        return this;
    }

    password() {
        this.rules.push(VALIDATION_RULES.PASSWORD);
        return this;
    }

    strongPassword() {
        this.rules.push(VALIDATION_RULES.STRONG_PASSWORD);
        return this;
    }

    phone() {
        this.rules.push(VALIDATION_RULES.PHONE);
        return this;
    }

    indianPhone() {
        this.rules.push(VALIDATION_RULES.INDIAN_PHONE);
        return this;
    }

    pan() {
        this.rules.push(VALIDATION_RULES.PAN);
        return this;
    }

    aadhaar() {
        this.rules.push(VALIDATION_RULES.AADHAAR);
        return this;
    }

    passport() {
        this.rules.push(VALIDATION_RULES.PASSPORT);
        return this;
    }

    workAuthNumber() {
        this.rules.push(VALIDATION_RULES.WORK_AUTH_NUMBER);
        return this;
    }

    ein() {
        this.rules.push(VALIDATION_RULES.EIN);
        return this;
    }

    taxId() {
        this.rules.push(VALIDATION_RULES.TAX_ID);
        return this;
    }

    gst() {
        this.rules.push(VALIDATION_RULES.GST);
        return this;
    }

    zipCode() {
        this.rules.push(VALIDATION_RULES.ZIP_CODE);
        return this;
    }

    indianPincode() {
        this.rules.push(VALIDATION_RULES.INDIAN_PINCODE);
        return this;
    }

    alphaOnly() {
        this.rules.push(VALIDATION_RULES.ALPHA_ONLY);
        return this;
    }

    alphanumeric() {
        this.rules.push(VALIDATION_RULES.ALPHANUMERIC);
        return this;
    }

    noSpecialChars() {
        this.rules.push(VALIDATION_RULES.NO_SPECIAL_CHARS);
        return this;
    }

    numeric() {
        this.rules.push(VALIDATION_RULES.NUMERIC);
        return this;
    }

    minValue(min) {
        this.rules.push(VALIDATION_RULES.MIN_VALUE(min));
        return this;
    }

    maxValue(max) {
        this.rules.push(VALIDATION_RULES.MAX_VALUE(max));
        return this;
    }

    range(min, max) {
        this.rules.push(VALIDATION_RULES.RANGE(min, max));
        return this;
    }

    minDate(date) {
        this.rules.push(VALIDATION_RULES.MIN_DATE(date));
        return this;
    }

    maxDate(date) {
        this.rules.push(VALIDATION_RULES.MAX_DATE(date));
        return this;
    }

    minAge(age) {
        this.rules.push(VALIDATION_RULES.MIN_AGE(age));
        return this;
    }

    fileRequired() {
        this.rules.push(VALIDATION_RULES.FILE_REQUIRED);
        return this;
    }

    fileType(types) {
        this.rules.push(VALIDATION_RULES.FILE_TYPE(types));
        return this;
    }

    fileSize(maxMB) {
        this.rules.push(VALIDATION_RULES.FILE_SIZE(maxMB));
        return this;
    }

    matchField(fieldName, label) {
        this.rules.push(VALIDATION_RULES.MATCH_FIELD(fieldName, label));
        return this;
    }

    dependsOn(fieldName, label) {
        this.rules.push(VALIDATION_RULES.DEPENDS_ON(fieldName, label));
        return this;
    }

    url() {
        this.rules.push(VALIDATION_RULES.URL);
        return this;
    }

    custom(validatorFn) {
        this.rules.push(VALIDATION_RULES.CUSTOM(validatorFn));
        return this;
    }

    validate(value, formData = {}) {
        for (const rule of this.rules) {
            const error = rule(value, formData);
            if (error) return error;
        }
        return null;
    }

    build() {
        return {
            fieldName: this.fieldName,
            label: this.label,
            validate: (value, formData) => this.validate(value, formData),
        };
    }
}

// Validation schema builder
export class ValidationSchema {
    constructor() {
        this.validators = {};
    }

    addField(fieldName, label) {
        const validator = new FieldValidator(fieldName, label);
        this.validators[fieldName] = validator;
        return validator;
    }

    validateField(fieldName, value, formData) {
        const validator = this.validators[fieldName];
        if (!validator) return null;
        return validator.validate(value, formData);
    }

    validateAll(formData) {
        const errors = {};
        let hasErrors = false;

        Object.keys(this.validators).forEach((fieldName) => {
            const error = this.validateField(fieldName, formData[fieldName], formData);
            if (error) {
                errors[fieldName] = error;
                hasErrors = true;
            }
        });

        return { errors, isValid: !hasErrors };
    }

    build() {
        return {
            validateField: (fieldName, value, formData) =>
                this.validateField(fieldName, value, formData),
            validateAll: (formData) => this.validateAll(formData),
        };
    }
}

// Utility functions using InputValidator
export const ValidationUtils = {
    isFormErrorEmpty: (errors) => InputValidator.isFormErrorEmpty(errors),
    isArrayEmpty: (arr) => InputValidator.isArrayEmpty(arr),
    isNull: (value) => InputValidator.isNull(value),
    isObjectEmpty: (obj) => InputValidator.isObjectEmpty(obj),
};